<template>
  <div class="home">
    <!-- Header -->
    <header class="home-header">
      <div class="hi-wrap">
        <div class="hi-title">Привет, {{ userName }} 👋</div>
        <div class="hi-sub">Ваш VPN под контролем</div>
      </div>
      <button class="avatar-btn" @click="goTo('profile')" aria-label="Профиль">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#0F1512" stroke-width="1.9"/><path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6" stroke="#0F1512" stroke-width="1.9" stroke-linecap="round"/></svg>
      </button>
    </header>

    <!-- Balance -->
    <div class="balance-card">
      <div class="balance-glow"></div>
      <div class="balance-top">
        <span class="balance-label">Баланс</span>
        <button class="history-btn" @click="handlePaymentHistoryClick" aria-label="История">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M8 6h11M8 12h11M8 18h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="4" cy="6" r="1.3" fill="currentColor"/><circle cx="4" cy="12" r="1.3" fill="currentColor"/><circle cx="4" cy="18" r="1.3" fill="currentColor"/></svg>
        </button>
      </div>
      <div class="balance-row">
        <div class="balance-amount" :class="{ skeleton: balance === null && !hasSessionData }">
          {{ balance !== null ? balance : 0 }} <span class="rub">₽</span>
        </div>
        <button class="coins-chip" @click="openRouletteFromCoins" aria-label="Монетки — открыть рулетку">
          <CoinIcon :size="30" />
          <span class="coins-chip-text">
            <span class="coins-chip-num">{{ coins !== null ? coins : '—' }}</span>
            <span class="coins-chip-cta">Крутить ›</span>
          </span>
        </button>
      </div>
      <button class="balance-cta" @click="handleDepositClick">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" stroke-width="2"/><path d="M2 10h20" stroke="currentColor" stroke-width="2"/></svg>
        Пополнить баланс
      </button>
    </div>

    <!-- Promo banners -->
    <div class="banners-section" v-if="banners.length">
      <div
        class="banners-container"
        ref="bannersContainer"
        @touchstart="handleBannerTouchStart"
        @touchmove="handleBannerTouchMove"
        @touchend="handleBannerTouchEnd"
      >
        <div class="banners-track" :style="{ transform: `translateX(${bannerOffset}px)` }">
          <div
            v-for="(banner, index) in banners"
            :key="index"
            class="banner-item"
            @click="handleBannerClick(banner)"
          >
            <!-- ✅ Рулетка — живой HTML-баннер (анимация сохраняется) -->
            <RouletteBanner v-if="banner.type === 'roulette'" />
            <img v-else :src="banner.image" :alt="banner.title" class="banner-image" />
          </div>
        </div>
        <div class="banner-dots" v-if="banners.length > 1">
          <div
            v-for="(banner, index) in banners"
            :key="index"
            :class="['dot', { active: currentBannerIndex === index }]"
            @click.stop="goToBanner(index)"
          ></div>
        </div>
      </div>
    </div>

    <!-- Subscriptions -->
    <div v-if="isInitialLoading" class="sub-card sub-skeleton"></div>

    <template v-else>
      <!-- Free tariff -->
      <div v-if="isFreeTariffAvailable" class="sub-card" ref="freeTariffRef">
        <div class="sub-inner">
          <div class="sub-row">
            <div class="sub-title">{{ freeTariff.tariff_name }}</div>
            <span class="badge-active">Активна</span>
          </div>
          <div class="chips">
            <span class="chip">🌍 Базовые страны</span>
            <span class="chip">♾️ Безлимит</span>
          </div>
          <div class="progress-wrap">
            <div class="progress-track"><div class="progress-fill" :style="{ width: trafficPercentage + '%' }"></div></div>
            <div class="progress-label">{{ freeTariff.tariff_left }}</div>
          </div>
          <div class="sub-actions">
            <button class="btn-connect" ref="connectButtonRef" @click="handleConnectionClick('FreeTariff')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12h6M12 9v6" stroke="#fff" stroke-width="2" stroke-linecap="round"/><path d="M10 6.5l1.3-1.3a3.5 3.5 0 0 1 5 5L15 11.5M14 17.5l-1.3 1.3a3.5 3.5 0 0 1-5-5L9 12.5" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>
              Подключить
            </button>
          </div>
        </div>
      </div>

      <!-- Paid tariffs: активные и недавно истёкшие -->
      <div v-for="(tariff, index) in visibleTariffs" :key="index" class="sub-card">
        <div class="sub-inner">
          <div class="sub-row">
            <div class="sub-title">{{ tariff.name }}</div>
            <span class="badge-active" :class="{ expired: tariff.daysRemaining <= 0 }">
              {{ tariff.daysRemaining > 0 ? 'Активна' : 'Истекла' }}
            </span>
          </div>
          <div class="chips">
            <span class="chip">🌍 {{ getCountriesText(tariff.name) }}</span>
            <span class="chip">♾️ Безлимит</span>
            <span class="chip" :class="{ 'chip-extra': extraSlotsFor(tariff.name) > 0 }">🖥 {{ getDevicesText(tariff.name) }}</span>
          </div>
          <div v-if="tariff.daysRemaining > 0" class="progress-wrap">
            <div class="progress-track"><div class="progress-fill" :style="{ width: calculateProgress(tariff.daysRemaining) + '%' }"></div></div>
            <div class="progress-label">{{ tariff.subscriptionEnd }}</div>
          </div>
          <div v-else class="expired-note">⏱ {{ tariff.subscriptionEnd }}</div>

          <!-- ✅ Продление для истёкших тарифов (ещё не в архиве) -->
          <button v-if="tariff.daysRemaining <= 0" class="btn-renew" @click="handleRenewClick(tariff)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 12a8 8 0 1 1-2.6-5.9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M20 4v4h-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Продлить {{ tariff.name }}
          </button>

          <!-- ✅ Подсказка о лимите устройств (на пределе / превышение) -->
          <div
            v-if="tariff.daysRemaining > 0 && deviceState(tariff.name) !== 'ok'"
            class="limit-alert"
            :class="deviceState(tariff.name)"
          >
            <span class="limit-alert-icon">{{ deviceState(tariff.name) === 'over' ? '⚠️' : '📶' }}</span>
            <div class="limit-alert-body">
              <div class="limit-alert-title">
                {{ deviceState(tariff.name) === 'over' ? 'Устройств больше нормы' : 'Достигнут лимит устройств' }}
              </div>
              <div class="limit-alert-text">
                {{ deviceState(tariff.name) === 'over'
                    ? `Подключено ${deviceCountFor(tariff.name)} из ${deviceLimitFor(tariff.name)} — лишние отключаются от VPN`
                    : `Занято ${deviceCountFor(tariff.name)} из ${deviceLimitFor(tariff.name)} — новое устройство не подключится` }}
              </div>
            </div>
          </div>

          <div v-if="tariff.daysRemaining > 0" class="sub-actions">
            <button class="btn-connect" @click="handleConnectionClick(tariff.name)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12h6M12 9v6" stroke="#fff" stroke-width="2" stroke-linecap="round"/><path d="M10 6.5l1.3-1.3a3.5 3.5 0 0 1 5 5L15 11.5M14 17.5l-1.3 1.3a3.5 3.5 0 0 1-5-5L9 12.5" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>
              Подключить
            </button>
            <!-- ✅ Кнопка устройств: подсвечивается при лимите/превышении -->
            <button
              class="btn-devices"
              :class="deviceState(tariff.name)"
              @click="openDevicesModal(tariff.name)"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="6" y="2" width="12" height="20" rx="3" stroke="currentColor" stroke-width="1.9"/><path d="M10 18.5h4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
              {{ deviceCountFor(tariff.name) || 0 }}
              <span v-if="deviceState(tariff.name) !== 'ok'" class="dev-warn-badge">!</span>
            </button>
          </div>

          <!-- ✅ Быстрый переход к покупке слотов при превышении -->
          <button
            v-if="tariff.daysRemaining > 0 && deviceState(tariff.name) !== 'ok'"
            class="btn-add-slots"
            :class="deviceState(tariff.name)"
            @click="openDevicesModal(tariff.name)"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="6" y="2" width="12" height="20" rx="3" stroke="currentColor" stroke-width="1.9"/><path d="M12 8v6M9 11h6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
            Добавить устройства · от {{ slotPriceFor(tariff.name, 1) }} ₽
          </button>

          <button
            v-if="tariff.daysRemaining > 0 && nextTariffName(tariff.name)"
            class="btn-upgrade"
            @click="openUpgrade(tariff)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 20V6M6 12l6-6 6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Улучшить до {{ nextTariffName(tariff.name) }}
          </button>
        </div>
      </div>

      <!-- ✅ Архив: тарифы, истёкшие больше недели назад -->
      <div v-if="archivedTariffs.length" class="archive">
        <button class="archive-head" @click="toggleArchive">
          <span class="archive-emoji">🗂</span>
          <div class="archive-head-info">
            <div class="archive-title">Архив · {{ archivedTariffs.length }}</div>
            <div class="archive-sub">Истекли больше недели назад</div>
          </div>
          <span class="archive-chevron" :class="{ open: archiveOpen }">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
        </button>

        <div v-if="archiveOpen" class="archive-list">
          <div v-for="(tariff, index) in archivedTariffs" :key="'arch-' + index" class="archive-card">
            <div class="archive-card-info">
              <div class="archive-card-name">{{ tariff.name }}</div>
              <div class="archive-card-date">⏱ Истёк {{ formatExpiryDate(tariff.expiry) }}</div>
            </div>
            <button class="archive-renew" @click="handleRenewClick(tariff)">Продлить</button>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!isFreeTariffAvailable && uniqueTariffs.length === 0" class="empty-state">
        <div class="empty-icon">📦</div>
        <div class="empty-title">Нет активных подписок</div>
        <div class="empty-text">Выберите тариф и начните пользоваться VPN</div>
        <button class="btn-buy" @click="handleBuySubscriptionClick">Купить подписку</button>
      </div>
    </template>

    <div class="legal-links">
      <a href="https://tma.avevpn.com/privacy_policy_page.html" target="_blank" rel="noopener noreferrer">Политика конфиденциальности</a>
      <span class="separator">•</span>
      <a href="https://tma.avevpn.com/user_agreement_avevpn.html" target="_blank" rel="noopener noreferrer">Соглашение</a>
    </div>

    <!-- Welcome Popup -->
    <WelcomePopup :is-visible="showWelcomePopup" @close="closeWelcomePopup" @open-tasks="openTasksFromPopup" />

    <!-- Onboarding Tour -->
    <Teleport to="body">
      <div v-if="showOnboardingTour" class="onboarding-overlay" @click="skipTour">
        <div class="onboarding-spotlight" :style="spotlightStyle"></div>
        <div class="onboarding-tooltip" :style="tooltipStyle">
          <div class="tooltip-content">
            <div class="tooltip-icon">👆</div>
            <div class="tooltip-title">Нажмите «Подключить»</div>
            <div class="tooltip-text">Активируйте свой бесплатный пробный период прямо сейчас</div>
            <button class="tooltip-btn" @click.stop="skipTour">Понятно</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Devices sheet -->
    <Teleport to="body">
      <div v-if="showDevicesModal" class="sheet-overlay" @click.self="closeDevicesModal">
        <div class="sheet">
          <div class="sheet-handle"></div>
          <div class="sheet-head">
            <div>
              <div class="sheet-title">📱 Устройства · {{ selectedTariffDisplayName }}</div>
              <div class="sheet-sub">
                {{ selectedExtraSlots > 0
                    ? `Базовый лимит ${baseLimitFor(selectedTariffName)} + докуплено ${selectedExtraSlots}`
                    : `Лимит тарифа — ${baseLimitFor(selectedTariffName)}` }}
              </div>
            </div>
            <button class="sheet-close" @click="closeDevicesModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
            </button>
          </div>

          <!-- ✅ Счётчик слотов -->
          <div class="slot-meter" :class="selectedDeviceState">
            <div class="slot-meter-top">
              <span>Занято слотов</span>
              <b>{{ selectedUsed }} из {{ selectedLimit }}</b>
            </div>
            <div class="slot-bars">
              <div
                v-for="(slot, i) in selectedSlotBars"
                :key="i"
                class="slot-bar"
                :class="slot"
              ></div>
            </div>
            <div class="slot-meter-hint">{{ selectedMeterHint }}</div>
          </div>

          <div v-if="isLoadingDevices && selectedDevices.length === 0" class="devices-loading">
            <div class="spinner"></div><span>Загрузка устройств…</span>
          </div>
          <div v-else-if="selectedDevices.length === 0" class="devices-empty">
            <div class="empty-icon">📭</div><div>Нет активных устройств</div>
          </div>
          <div v-else class="devices-list">
            <div
              v-for="(device, i) in selectedDevices"
              :key="device.hwid + device.userUuid"
              class="device-card"
              :class="{ blocked: i >= selectedLimit }"
            >
              <div class="device-icon">{{ getPlatformIcon(device.platform) }}</div>
              <div class="device-info">
                <div class="device-model">{{ device.deviceModel || 'Устройство' }}</div>
                <div class="device-meta">{{ device.platform }} {{ device.osVersion }} · {{ getAppName(device.userAgent) }} · {{ formatDeviceDate(device.updatedAt) }}</div>
              </div>
              <span v-if="i >= selectedLimit" class="device-blocked-tag">Вне лимита</span>
              <div class="device-actions">
                <template v-if="confirmDeleteHwid === device.hwid">
                  <button class="dev-yes" :disabled="deletingHwid === device.hwid" @click="deleteDevice(device.hwid)">
                    <span v-if="deletingHwid === device.hwid" class="spinner-sm"></span><span v-else>✓</span>
                  </button>
                  <button class="dev-no" @click="cancelDelete">✕</button>
                </template>
                <button v-else class="dev-del" @click="requestDelete(device.hwid)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
            </div>
          </div>

          <!-- ✅ Покупка дополнительных слотов -->
          <div class="slots-buy">
            <div class="slots-buy-head">
              <span class="slots-buy-emoji">➕</span>
              <div>
                <div class="slots-buy-title">Добавить слоты устройств</div>
                <div class="slots-buy-sub">{{ SLOT_MONTH }} ₽ за устройство в месяц · пропорционально остатку</div>
              </div>
            </div>

            <div class="stepper">
              <button class="step-btn" :class="{ off: slotsToBuy <= 1 }" @click="decSlots">−</button>
              <div class="step-value">
                <div class="step-num">+{{ slotsToBuy }}</div>
                <div class="step-hint">{{ selectedMaxAdd > 0 ? `можно ещё ${selectedMaxAdd}` : 'достигнут максимум' }}</div>
              </div>
              <button class="step-btn" :class="{ off: slotsToBuy >= selectedMaxAdd }" @click="incSlots">+</button>
            </div>

            <div class="slots-summary">
              <div class="slots-row">
                <span>Станет слотов</span>
                <b>{{ selectedLimit + slotsToBuy }}</b>
              </div>
              <div class="slots-row">
                <span>К оплате за остаток ({{ selectedRemainingDays }} дн.)</span>
                <b class="big">{{ slotsPrice }} ₽</b>
              </div>
              <div class="slots-row muted">
                <span>Баланс после</span>
                <b :class="{ danger: !slotsCanAfford }">{{ slotsAfterBalance }} ₽</b>
              </div>
            </div>

            <button
              class="slots-cta"
              :class="{ disabled: !slotsCanAfford || slotsLoading || selectedMaxAdd <= 0 }"
              :disabled="!slotsCanAfford || slotsLoading || selectedMaxAdd <= 0"
              @click="confirmBuySlots"
            >
              <span v-if="slotsLoading" class="spinner-sm"></span>
              <span v-else-if="selectedMaxAdd <= 0">Достигнут максимум слотов</span>
              <span v-else-if="!slotsCanAfford">Недостаточно средств</span>
              <span v-else>Купить {{ slotsToBuy }} {{ slotWord(slotsToBuy) }} · {{ slotsPrice }} ₽</span>
            </button>
            <div class="slots-note">Слоты действуют до конца текущей подписки и продлеваются вместе с ней</div>
          </div>

          <div class="sheet-hint">Удалённое устройство потеряет доступ к VPN до повторного подключения</div>
        </div>
      </div>
    </Teleport>

    <!-- Upgrade sheet -->
    <Teleport to="body">
      <div v-if="upgradeOpen" class="sheet-overlay" @click.self="closeUpgrade">
        <div class="sheet">
          <div class="sheet-handle"></div>
          <div class="sheet-title">Улучшить тариф</div>
          <div class="sheet-sub">Доплата рассчитывается пропорционально остатку · {{ upgradeRemainingDays }} дней</div>

          <div class="up-current">
            <span class="up-emoji">{{ emojiByKey(upgradeFromKey) }}</span>
            <div class="up-current-info">
              <div class="up-label">Текущий тариф</div>
              <div class="up-name">{{ upgradeFromKey }}</div>
            </div>
            <span class="up-arrow">→</span>
          </div>

          <button class="up-option selected">
            <span class="up-emoji">{{ emojiByKey(upgradeToKey) }}</span>
            <div class="up-option-info">
              <div class="up-option-name">{{ upgradeToKey }}</div>
              <div class="up-option-desc">Больше стран, устройств и скорости</div>
            </div>
            <div class="up-option-price">
              <div class="up-surcharge">+{{ upgradeProration.base }} ₽</div>
              <div class="up-surcharge-label">доплата</div>
            </div>
          </button>

          <div class="up-bypass" @click="toggleUpgradeBypass">
            <span class="up-emoji">🛡️</span>
            <div class="up-option-info">
              <div class="up-option-name sm">Обход белых списков</div>
              <div class="up-option-desc">+{{ upgradeBypassSurcharge }} ₽ к доплате</div>
            </div>
            <div class="toggle" :class="{ on: upgradeBypass }"><span class="knob"></span></div>
          </div>

          <div class="up-summary">
            <div class="up-summary-row">
              <span>Доплата с баланса</span>
              <b>{{ upgradeProration.total }} ₽</b>
            </div>
            <div class="up-summary-row muted">
              <span>Баланс после</span>
              <b :class="{ danger: !upgradeCanAfford }">{{ upgradeAfterBalance }} ₽</b>
            </div>
          </div>

          <button
            class="up-cta"
            :class="{ disabled: !upgradeCanAfford || upgradeLoading }"
            :disabled="!upgradeCanAfford || upgradeLoading"
            @click="confirmUpgrade"
          >
            <span v-if="upgradeLoading" class="spinner-sm"></span>
            <span v-else>{{ upgradeCanAfford ? 'Улучшить · ' + upgradeProration.total + ' ₽' : 'Недостаточно средств' }}</span>
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Toast -->
    <Teleport to="body">
      <div v-if="toast.show" class="toast">{{ toast.msg }}</div>
    </Teleport>

    <!-- ✅ Монеты за ежедневный вход (POST /coins/checkin) -->
    <Teleport to="body">
      <transition name="coin-toast">
        <button v-if="coinToast.show" class="coin-toast" @click="openRouletteFromToast">
          <CoinIcon :size="34" class="coin-toast-icon" />
          <div class="coin-toast-body">
            <div class="coin-toast-title">+{{ coinToast.amount }} монет за вход</div>
            <div class="coin-toast-sub">На счету {{ coinToast.total }} · крутите рулетку ›</div>
          </div>
        </button>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted, computed, watch, nextTick} from "vue";
import {useRouter, useRoute} from "vue-router";
import {haptic, disableSwipes} from "@/utils/telegram";
import WelcomePopup from "@/components/WelcomePopup.vue";
import { fetchAndCacheUserInfo } from '@/services/userService';
import RouletteBanner from '@/components/roulette/RouletteBanner.vue';
import CoinIcon from '@/components/roulette/CoinIcon.vue';
import { dailyCheckin, moscowDay, setCachedCoins, getCachedCoins, getCoinsBalance, CHECKIN_DAY_KEY } from '@/services/coinsService';

const router = useRouter();
const route = useRoute();

const balance = ref(null);
const freeTariff = ref(null);
const uniqueTariffs = ref([]);
const showWelcomePopup = ref(false);
const showOnboardingTour = ref(false);
const botName = ref("Prometei");

const activeTab = ref('subscriptions');
const whiteList = ref(null);
const shouldAnimateWhitelist = ref(false);
const whitelistProgress = ref(0);

const hasSessionData = ref(false);
const isInitialLoading = ref(true);
const isRefreshing = ref(false);

const connectButtonRef = ref(null);
const freeTariffRef = ref(null);

const isNotificationDismissed = ref(false);
const swipeOffset = ref(0);
const notificationOpacity = ref(1);
const touchStartX = ref(0);
const notificationRef = ref(null);

const currentBannerIndex = ref(0);
const bannerOffset = ref(0);
const bannerTouchStartX = ref(0);
const bannerTouchCurrentX = ref(0);
const isBannerSwiping = ref(false);
const bannerAutoplayInterval = ref(null);
const bannersContainer = ref(null);

const spotlightStyle = ref({});
const tooltipStyle = ref({});

// ===== Управление устройствами (HWID) =====
const API_BASE = 'https://baaack.zabanishumresh.com';

const devices = ref([]);
const isLoadingDevices = ref(false);
const showDevicesModal = ref(false);
const selectedTariffName = ref('');
const selectedTariffDisplayName = ref('');
const confirmDeleteHwid = ref(null);
const deletingHwid = ref(null);

/* ============================================================
   ✅ ЛИМИТЫ УСТРОЙСТВ И ПОКУПКА ДОПОЛНИТЕЛЬНЫХ СЛОТОВ
   ============================================================ */
const DEVICE_LIMITS = { Basic: 1, Medium: 3, Premium: 10 }; // базовый максимум тарифа
const MAX_EXTRA_SLOTS = 10;                                 // сколько можно докупить сверху
const SLOT_MONTH = 50;                                      // ₽ за 1 устройство в месяц

// Докупленные слоты по тарифам (приходят с бэка, кэшируются в sessionStorage)
const extraSlots = ref({ Basic: 0, Medium: 0, Premium: 0 });

const slotsToBuy = ref(1);
const slotsLoading = ref(false);

function deviceWord(n) {
  const d = n % 10, h = n % 100;
  if (d === 1 && h !== 11) return 'устройство';
  if ([2, 3, 4].includes(d) && ![12, 13, 14].includes(h)) return 'устройства';
  return 'устройств';
}
function slotWord(n) {
  const d = n % 10, h = n % 100;
  if (d === 1 && h !== 11) return 'слот';
  if ([2, 3, 4].includes(d) && ![12, 13, 14].includes(h)) return 'слота';
  return 'слотов';
}

const baseLimitFor = (key) => DEVICE_LIMITS[key] ?? 1;
const extraSlotsFor = (displayName) => {
  const key = getTariffKey(displayName);
  return key ? (extraSlots.value[key] || 0) : 0;
};
const deviceLimitFor = (displayName) => {
  const key = getTariffKey(displayName);
  if (!key) return 0;
  return baseLimitFor(key) + (extraSlots.value[key] || 0);
};
// 'ok' | 'limit' (ровно на лимите) | 'over' (больше нормы)
const deviceState = (displayName) => {
  const limit = deviceLimitFor(displayName);
  const used = deviceCountFor(displayName);
  if (!limit) return 'ok';
  if (used > limit) return 'over';
  if (used === limit) return 'limit';
  return 'ok';
};
// Пропорционально остатку, как в апгрейде: цена/мес × (дни / 30)
const remainingDaysFor = (displayName) => {
  const t = uniqueTariffs.value.find(x => x.name === displayName || getTariffKey(x.name) === getTariffKey(displayName));
  return Math.max(1, Math.floor(t?.daysRemaining || 1));
};
const slotPriceFor = (displayName, count) =>
  Math.round(SLOT_MONTH * count * remainingDaysFor(displayName) / 30);

const selectedExtraSlots = computed(() => extraSlots.value[selectedTariffName.value] || 0);
const selectedLimit = computed(() => baseLimitFor(selectedTariffName.value) + selectedExtraSlots.value);
const selectedUsed = computed(() => selectedDevices.value.length);
const selectedDeviceState = computed(() => {
  if (selectedUsed.value > selectedLimit.value) return 'over';
  if (selectedUsed.value === selectedLimit.value) return 'limit';
  return 'ok';
});
const selectedMaxAdd = computed(() => Math.max(0, MAX_EXTRA_SLOTS - selectedExtraSlots.value));
const selectedRemainingDays = computed(() => remainingDaysFor(selectedTariffDisplayName.value));
const slotsPrice = computed(() =>
  Math.round(SLOT_MONTH * slotsToBuy.value * selectedRemainingDays.value / 30)
);
const slotsCanAfford = computed(() => (balance.value ?? 0) >= slotsPrice.value);
const slotsAfterBalance = computed(() => Math.max(0, (balance.value ?? 0) - slotsPrice.value));

const selectedSlotBars = computed(() => {
  const base = baseLimitFor(selectedTariffName.value);
  const limit = selectedLimit.value;
  const used = selectedUsed.value;
  const total = Math.max(limit, used);
  const bars = [];
  for (let i = 0; i < total; i++) {
    if (i >= limit) bars.push('beyond');
    else if (i < used) bars.push(i >= base ? 'filled-extra' : 'filled');
    else bars.push('free');
  }
  return bars;
});

const selectedMeterHint = computed(() => {
  const over = selectedUsed.value - selectedLimit.value;
  if (over > 0) {
    return `${over} ${deviceWord(over)} сверх лимита не ${over === 1 ? 'получит' : 'получат'} доступ. Удалите лишние или докупите слоты.`;
  }
  if (selectedDeviceState.value === 'limit') {
    return 'Свободных слотов нет — докупите слот, чтобы подключить ещё устройство.';
  }
  return `Свободных слотов: ${selectedLimit.value - selectedUsed.value}`;
});

const decSlots = () => { haptic.impact(); slotsToBuy.value = Math.max(1, slotsToBuy.value - 1); };
const incSlots = () => { haptic.impact(); slotsToBuy.value = Math.min(selectedMaxAdd.value, slotsToBuy.value + 1); };

/* ✅ Разбор поля extra_devices из /requestGetAllUserInfoWithTasks
   Формат: JSON-строка вида {"Basic": [ {...}, {...} ], "Medium": [...] }
   Количество элементов массива = сколько доп. устройств куплено для тарифа. */
const parseExtraDevices = (raw) => {
  if (!raw) return null;
  let data = raw;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch (e) {
      console.error('❌ Ошибка парсинга extra_devices:', e);
      return null;
    }
  }
  if (!data || typeof data !== 'object') return null;

  const result = { Basic: 0, Medium: 0, Premium: 0 };
  Object.keys(data).forEach((rawKey) => {
    const k = String(rawKey).trim().toLowerCase();
    const key = k.charAt(0).toUpperCase() + k.slice(1);
    if (!(key in result)) return;
    const val = data[rawKey];
    result[key] = Array.isArray(val) ? val.length : (parseInt(val, 10) || 0);
  });
  return result;
};

const persistExtraSlots = () => {
  try { sessionStorage.setItem('extra_device_slots', JSON.stringify(extraSlots.value)); } catch (e) { /* noop */ }
};

// Одна покупка = одно дополнительное устройство (POST /buyExtraDevice)
const buyExtraDeviceRequest = (tariffKey) => fetch(`${API_BASE}/buyExtraDevice`, {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: buildAuthHeader()   // авторизация как в /hwid/devices и /upgradeTariff
  },
  body: JSON.stringify({ tariff_name: tariffKey })
});

const slotErrorText = (status) => {
  if (status === 400) return 'Некорректный тариф';
  if (status === 401 || status === 403) return 'Сессия истекла, откройте приложение заново';
  if (status === 402) return 'Недостаточно средств на балансе';
  if (status === 404) return 'Профиль не найден';
  if (status === 406) return 'Тариф не активен';
  if (status === 409) return 'Достигнут максимум устройств';
  return 'Не удалось купить слоты';
};

const confirmBuySlots = async () => {
  const key = selectedTariffName.value;
  const count = slotsToBuy.value;
  const price = slotsPrice.value;
  if (!key || count < 1) return;
  if (count > selectedMaxAdd.value) { showToast('Достигнут максимум слотов'); return; }
  if (!slotsCanAfford.value) { showToast('Недостаточно средств на балансе'); return; }

  slotsLoading.value = true;
  let bought = 0;
  let failStatus = null;

  try {
    // Ручка покупает по одному устройству — повторяем нужное число раз
    for (let i = 0; i < count; i++) {
      const res = await buyExtraDeviceRequest(key);
      if (res.status === 200) {
        bought++;
      } else {
        failStatus = res.status;
        console.error(`❌ /buyExtraDevice → HTTP ${res.status}`);
        break;
      }
    }

    if (bought > 0) {
      const spent = Math.round(price * bought / count);
      const newBal = Math.max(0, (balance.value ?? 0) - spent);
      balance.value = newBal;
      sessionStorage.setItem('balance', String(newBal));
      extraSlots.value = { ...extraSlots.value, [key]: (extraSlots.value[key] || 0) + bought };
      persistExtraSlots();
      slotsToBuy.value = 1;
      haptic.impact();
      showToast(`+${bought} ${slotWord(bought)} · −${spent} ₽`);
      sendMetric(`BuyExtraDevice-${key}-${bought}`);
      fetchDevices();
      refreshDataInBackground(); // подтягиваем актуальный баланс и лимиты с бэка
    }

    if (failStatus !== null) {
      showToast(bought > 0
        ? `Куплено ${bought} из ${count} · ${slotErrorText(failStatus)}`
        : slotErrorText(failStatus));
    }
  } catch (e) {
    console.error('❌ Ошибка покупки слотов:', e);
    showToast('Ошибка сети, попробуйте позже');
  } finally {
    slotsLoading.value = false;
  }
};

// Banners data
const banners = ref([
  { id: 0, type: 'roulette', title: 'Рулетка', route: 'roulette' }
]);

const isFreeTariffAvailable = computed(() => {
  return freeTariff.value && freeTariff.value.tariff_name;
});

const trafficPercentage = computed(() => {
  if (!freeTariff.value) return 0;

  const totalDays = 3;
  const totalHours = totalDays * 24;
  const tariffLeftString = freeTariff.value.tariff_left;

  const parts = tariffLeftString.split(' ');
  if (parts.length < 4) return 0;

  const daysLeft = parseInt(parts[0], 10) || 0;
  const hoursLeft = parseInt(parts[2], 10) || 0;

  const totalLeftHours = daysLeft * 24 + hoursLeft;
  let percentage = (totalLeftHours / totalHours) * 100;
  const buffer = Math.min(percentage * 0.1, 60);

  return Math.max(0, percentage - buffer);
});

const trafficBarClass = computed(() => {
  const daysLeft = parseInt(freeTariff.value?.tariff_left || 0);
  if (daysLeft <= 1) return 'danger';
  else if (daysLeft === 2) return 'warning';
  else return 'success';
});

const shouldShowTariffNotification = computed(() => {
  return uniqueTariffs.value.some(tariff => {
    const isExpired = tariff.daysRemaining <= 0;
    const isExpiringSoon = tariff.daysRemaining > 0 && tariff.daysRemaining <= 7;
    return isExpired || isExpiringSoon;
  });
});

const notificationTariff = computed(() => {
  const expired = uniqueTariffs.value.find(t => t.daysRemaining <= 0);
  if (expired) return expired;
  return uniqueTariffs.value.find(t => t.daysRemaining > 0 && t.daysRemaining <= 7);
});

const notificationTitle = computed(() => {
  if (!notificationTariff.value) return '';
  if (notificationTariff.value.daysRemaining <= 0) return 'Тариф истёк';
  return 'Тариф скоро истечёт';
});

const notificationText = computed(() => {
  if (!notificationTariff.value) return '';
  const tariffName = notificationTariff.value.name;
  if (notificationTariff.value.daysRemaining <= 0) {
    return `Тариф "${tariffName}" истёк. Продлите подписку`;
  }
  const days = notificationTariff.value.daysRemaining;
  const daysWord = getDaysWord(days);
  return `Тариф "${tariffName}" истекает через ${days} ${daysWord}`;
});

const isNewUser = computed(() => {
  const welcomeShown = localStorage.getItem("welcomeShown");
  if (welcomeShown === "true") return false;

  const firstLogin = sessionStorage.getItem("first_login");
  if (!firstLogin) return false;

  const firstLoginTime = parseInt(firstLogin) * 1000;
  const currentTime = Date.now();
  const threeHoursInMs = 3 * 60 * 60 * 1000;

  return (currentTime - firstLoginTime) <= threeHoursInMs;
});

// ===== Логика устройств (HWID) =====
const buildAuthHeader = () => {
  const token = sessionStorage.getItem('jwtToken') || '';
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
};

const getDeviceTariff = (device) => {
  const t = device?._tariff_username || '';
  if (t.endsWith('Premium')) return 'Premium';
  if (t.endsWith('Medium')) return 'Medium';
  if (t.endsWith('Basic')) return 'Basic';
  return 'Unknown';
};

const getTariffKey = (displayName) => {
  if (!displayName) return null;
  if (displayName.includes('Premium')) return 'Premium';
  if (displayName.includes('Medium')) return 'Medium';
  if (displayName.includes('Basic')) return 'Basic';
  return null;
};

const deviceCountByTariff = computed(() => {
  const counts = { Basic: 0, Medium: 0, Premium: 0 };
  devices.value.forEach(d => {
    const key = getDeviceTariff(d);
    if (counts[key] !== undefined) counts[key]++;
  });
  return counts;
});

const deviceCountFor = (displayName) => {
  const key = getTariffKey(displayName);
  return key ? deviceCountByTariff.value[key] : 0;
};

/* ============================================================
   ✅ СОРТИРОВКА ТАРИФОВ + АРХИВ
   Активные — сверху (ближе к истечению выше), затем недавно
   истёкшие (до 7 дней), а всё что старше недели — в архив.
   ============================================================ */
const ARCHIVE_AFTER_DAYS = 7;
const archiveOpen = ref(false);

const toggleArchive = () => {
  haptic.impact();
  archiveOpen.value = !archiveOpen.value;
  sendMetric(archiveOpen.value ? 'Archive-Open' : 'Archive-Close');
};

const activeTariffs = computed(() =>
  uniqueTariffs.value
    .filter(t => t.daysRemaining > 0)
    .sort((a, b) => a.daysRemaining - b.daysRemaining)
);

// Истекли недавно (до недели назад) — остаются в основном списке, под активными
const recentlyExpiredTariffs = computed(() =>
  uniqueTariffs.value
    .filter(t => t.daysRemaining <= 0 && t.daysRemaining > -ARCHIVE_AFTER_DAYS)
    .sort((a, b) => b.daysRemaining - a.daysRemaining)
);

const archivedTariffs = computed(() =>
  uniqueTariffs.value
    .filter(t => t.daysRemaining <= -ARCHIVE_AFTER_DAYS)
    .sort((a, b) => (b.expiry || 0) - (a.expiry || 0))
);

const visibleTariffs = computed(() => [...activeTariffs.value, ...recentlyExpiredTariffs.value]);

const formatExpiryDate = (expiry) => {
  if (!expiry) return '';
  try {
    return new Date(expiry * 1000).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch {
    return '';
  }
};

// Архив свёрнут, если есть хотя бы один активный тариф
watch(activeTariffs, (list) => {
  if (list.length > 0) archiveOpen.value = false;
  else if (archivedTariffs.value.length) archiveOpen.value = true;
}, { immediate: true });

const selectedDevices = computed(() => {
  if (!selectedTariffName.value) return [];
  return devices.value.filter(d => getDeviceTariff(d) === selectedTariffName.value);
});

const fetchDevices = async () => {
  const token = sessionStorage.getItem('jwtToken');
  if (!token) {
    console.error('JWT токен не найден для загрузки устройств');
    return;
  }
  isLoadingDevices.value = true;
  try {
    const res = await fetch(`${API_BASE}/hwid/devices`, {
      headers: { accept: 'application/json', Authorization: buildAuthHeader() }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    devices.value = await res.json();
    console.log('✅ Устройства загружены:', devices.value.length);
  } catch (e) {
    console.error('❌ Ошибка загрузки устройств:', e);
  } finally {
    isLoadingDevices.value = false;
  }
};

const openDevicesModal = (displayName) => {
  haptic.impact();
  const key = getTariffKey(displayName);
  if (!key) return;
  selectedTariffName.value = key;
  selectedTariffDisplayName.value = displayName;
  confirmDeleteHwid.value = null;
  slotsToBuy.value = 1;
  showDevicesModal.value = true;
  sendMetric(`ViewDevices-${key}`);
  fetchDevices();
};

const closeDevicesModal = () => {
  showDevicesModal.value = false;
  confirmDeleteHwid.value = null;
  selectedTariffName.value = '';
  selectedTariffDisplayName.value = '';
};

const requestDelete = (hwid) => {
  haptic.impact();
  confirmDeleteHwid.value = hwid;
};

const cancelDelete = () => {
  confirmDeleteHwid.value = null;
};

const deleteDevice = async (hwid) => {
  deletingHwid.value = hwid;
  confirmDeleteHwid.value = null;
  try {
    const res = await fetch(`${API_BASE}/hwid/devices/delete`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: buildAuthHeader()
      },
      body: JSON.stringify({ hwid })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    haptic.impact();
    sendMetric(`DeleteDevice-${selectedTariffName.value}`);
    await fetchDevices();
  } catch (e) {
    console.error('❌ Ошибка удаления устройства:', e);
  } finally {
    deletingHwid.value = null;
  }
};

const getPlatformIcon = (platform) => {
  const p = (platform || '').toLowerCase();
  if (p.includes('ios')) return '📱';
  if (p.includes('mac')) return '💻';
  if (p.includes('win')) return '🖥';
  if (p.includes('android')) return '🤖';
  return '📲';
};

const getAppName = (ua) => (ua ? (ua.split('/')[0] || ua) : 'Неизвестно');

const formatDeviceDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch {
    return '';
  }
};

const switchTab = (tab) => {
  haptic.impact();
  const previousTab = activeTab.value;
  activeTab.value = tab;
  sendMetric(`SwitchTab-${tab}`);

  if (tab === 'whitelist' && previousTab !== 'whitelist') {
    shouldAnimateWhitelist.value = false;
    whitelistProgress.value = 0;
    nextTick(() => {
      setTimeout(() => {
        shouldAnimateWhitelist.value = true;
        whitelistProgress.value = calculateWhitelistProgress();
      }, 100);
    });
  }
};

const calculateWhitelistProgress = () => {
  if (!whiteList.value || whiteList.value.total_purchased === 0) return 0;
  return Math.min(100, (whiteList.value.gb_left / whiteList.value.total_purchased) * 100);
};

const handleWhitelistConnection = () => {
  sendMetric('Connect-Whitelist');
  goTo('connection');
};

const handleBuyWhitelist = () => {
  sendMetric('BuyWhitelist');
  goTo('buyVPN');
};

watch(() => route.path, (newPath, oldPath) => {
  if (oldPath === '/' && newPath !== '/') {
    if (showWelcomePopup.value) {
      showWelcomePopup.value = false;
      localStorage.setItem("welcomeShown", "true");
    }
    if (showOnboardingTour.value) showOnboardingTour.value = false;
    if (showDevicesModal.value) closeDevicesModal();
    if (upgradeOpen.value) upgradeOpen.value = false;
  }
});

const getCountriesText = (tariffName) => {
  if (tariffName.includes('Basic')) return '6 серверов';
  if (tariffName.includes('Medium')) return '8 серверов';
  if (tariffName.includes('Premium')) return '9 серверов';
  return 'Несколько стран';
};

// ✅ Учитывает докупленные слоты
const getDevicesText = (tariffName) => {
  const limit = deviceLimitFor(tariffName);
  const extra = extraSlotsFor(tariffName);
  if (!limit) return '1 устройство';
  return extra > 0
    ? `${limit} ${deviceWord(limit)} (+${extra})`
    : `${limit} ${deviceWord(limit)}`;
};

const calculateProgress = (daysRemaining) => {
  const maxDays = 30;
  return Math.min(100, (daysRemaining / maxDays) * 100);
};

const addEmojiToTariff = (tariffName) => {
  if (tariffName.includes('Free')) return 'Free 🌱';
  if (tariffName.includes('Basic')) return 'Basic 🧵';
  if (tariffName.includes('Medium')) return 'Medium 🚀';
  if (tariffName.includes('Premium')) return 'Premium 💎';
  return tariffName;
};

// Banner functions
const getBannerWidth = () => {
  if (!bannersContainer.value) return 0;
  return bannersContainer.value.offsetWidth;
};

const updateBannerOffset = () => {
  bannerOffset.value = -currentBannerIndex.value * getBannerWidth();
};

const handleBannerTouchStart = (e) => {
  stopBannerAutoplay();
  isBannerSwiping.value = true;
  bannerTouchStartX.value = e.touches[0].clientX;
  bannerTouchCurrentX.value = e.touches[0].clientX;
};

const handleBannerTouchMove = (e) => {
  if (!isBannerSwiping.value) return;
  bannerTouchCurrentX.value = e.touches[0].clientX;
  const diff = bannerTouchCurrentX.value - bannerTouchStartX.value;
  bannerOffset.value = -currentBannerIndex.value * getBannerWidth() + diff;
};

const handleBannerTouchEnd = () => {
  if (!isBannerSwiping.value) return;
  const diff = bannerTouchCurrentX.value - bannerTouchStartX.value;
  const threshold = getBannerWidth() * 0.3;

  if (Math.abs(diff) > threshold) {
    if (diff > 0 && currentBannerIndex.value > 0) currentBannerIndex.value--;
    else if (diff < 0 && currentBannerIndex.value < banners.value.length - 1) currentBannerIndex.value++;
  }

  updateBannerOffset();
  isBannerSwiping.value = false;
  startBannerAutoplay();
};

const goToBanner = (index) => {
  stopBannerAutoplay();
  currentBannerIndex.value = index;
  updateBannerOffset();
  startBannerAutoplay();
};

const nextBanner = () => {
  currentBannerIndex.value = (currentBannerIndex.value + 1) % banners.value.length;
  updateBannerOffset();
};

const startBannerAutoplay = () => {
  stopBannerAutoplay();
  if (banners.value.length < 2) return;
  bannerAutoplayInterval.value = setInterval(nextBanner, 5000);
};

const stopBannerAutoplay = () => {
  if (bannerAutoplayInterval.value) {
    clearInterval(bannerAutoplayInterval.value);
    bannerAutoplayInterval.value = null;
  }
};

const handleBannerClick = (banner) => {
  haptic.impact();
  sendMetric(`Banner-${banner.title}`);
  router.push(`/${banner.route}`);
};

const loadDataFromSession = () => {
  const sessionBalance = sessionStorage.getItem("balance");
  const sessionAllTariffs = sessionStorage.getItem("all_tariffs");
  const sessionWhiteList = sessionStorage.getItem("white_list");
  const sessionSlots = sessionStorage.getItem("extra_device_slots");
  // ✅ Сырое поле с бэка (может лежать отдельным ключом или внутри user_info)
  let sessionExtraDevices = sessionStorage.getItem("extra_devices");
  if (!sessionExtraDevices) {
    try {
      const info = JSON.parse(sessionStorage.getItem("user_info") || 'null');
      if (info?.extra_devices) sessionExtraDevices = info.extra_devices;
    } catch (e) { /* noop */ }
  }

  if (sessionBalance !== null) {
    balance.value = parseFloat(sessionBalance);
    hasSessionData.value = true;
  }

  // ✅ Докупленные слоты из кэша
  if (sessionSlots) {
    try {
      const parsed = JSON.parse(sessionSlots);
      extraSlots.value = {
        Basic: parsed.Basic || parsed.basic || 0,
        Medium: parsed.Medium || parsed.medium || 0,
        Premium: parsed.Premium || parsed.premium || 0
      };
    } catch (e) {
      console.error('❌ Ошибка парсинга extra_device_slots:', e);
    }
  }

  // ✅ Данные с ручки важнее кэша
  const fromApi = parseExtraDevices(sessionExtraDevices);
  if (fromApi) {
    extraSlots.value = fromApi;
    persistExtraSlots();
  }

  if (sessionWhiteList) {
    try {
      whiteList.value = JSON.parse(sessionWhiteList);
    } catch (error) {
      console.error("❌ Ошибка при парсинге white_list:", error);
    }
  }

  if (sessionAllTariffs) {
    try {
      const tariffsData = JSON.parse(sessionAllTariffs);
      const tempTariffs = [];
      const slotsFromApi = { ...extraSlots.value };

      ['basic', 'medium', 'premium'].forEach(type => {
        // ✅ Докупленные слоты могут прийти и внутри самого тарифа (массив или число)
        if (!fromApi) {
          const extra = tariffsData[type]?.extra_devices ?? tariffsData[type]?.extra_slots;
          if (extra !== undefined && extra !== null) {
            slotsFromApi[type.charAt(0).toUpperCase() + type.slice(1)] =
                Array.isArray(extra) ? extra.length : (parseInt(extra, 10) || 0);
          }
        }

        if (tariffsData[type]?.expiry) {
          const expiry = parseInt(tariffsData[type].expiry);
          const endDate = new Date(expiry * 1000);
          const timeDifference = endDate - new Date();
          const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
          const tariffName = type.charAt(0).toUpperCase() + type.slice(1);

          tempTariffs.push({
            name: addEmojiToTariff(tariffName),
            expiry: expiry,
            subscription_link: tariffsData[type].link || '',
            daysRemaining: daysRemaining,
            subscriptionEnd: daysRemaining > 0
                ? `Осталось ${daysRemaining} ${getDaysWord(daysRemaining)}`
                : "Подписка истекла"
          });
        }
      });

      extraSlots.value = slotsFromApi;
      persistExtraSlots();
      uniqueTariffs.value = tempTariffs;

      if (tariffsData.free?.expiry) {
        const freeExpiry = parseInt(tariffsData.free.expiry);
        const currentTime = Math.floor(Date.now() / 1000);
        const timeLeft = freeExpiry - currentTime;

        if (timeLeft > 0) {
          const daysLeft = Math.floor(timeLeft / 86400);
          const hoursLeft = Math.floor((timeLeft % 86400) / 3600);

          freeTariff.value = {
            tariff_name: "Free 🌱",
            tariff_left: `${daysLeft} дней ${hoursLeft} часов`,
            subscription_link: tariffsData.free.link || ''
          };
        }
      }

      hasSessionData.value = true;
    } catch (error) {
      console.error("Ошибка при загрузке тарифов из sessionStorage:", error);
    }
  }

  isInitialLoading.value = false;
};

/* ============================================================
   ✅ МОНЕТЫ: ЕЖЕДНЕВНЫЙ ЧЕКИН
   POST /coins/checkin — один раз в день по Москве (+5 монет).
   Вызываем при открытии главной; already_done → ничего не показываем.
   ============================================================ */
// ✅ Баланс монеток в карточке баланса
const coins = ref(getCachedCoins());
const loadCoins = async () => {
  if (!sessionStorage.getItem('jwtToken')) return;
  try { coins.value = await getCoinsBalance(); } catch (e) { console.error('❌ Ошибка загрузки монет:', e); }
};
const openRouletteFromCoins = () => {
  sendMetric('Balance-Coins');
  goTo('roulette');
};

const coinToast = ref({ show: false, amount: 0, total: 0 });
let coinToastTimer = null;
let checkinInFlight = false;

const runDailyCheckin = async () => {
  if (checkinInFlight) return;
  const today = moscowDay();
  try { if (sessionStorage.getItem(CHECKIN_DAY_KEY) === today) return; } catch (e) { /* noop */ }
  if (!sessionStorage.getItem('jwtToken')) return;

  checkinInFlight = true;
  try {
    const r = await dailyCheckin();
    sessionStorage.setItem(CHECKIN_DAY_KEY, today);
    if (typeof r?.coins_total === 'number') { setCachedCoins(r.coins_total); coins.value = r.coins_total; }
    if (r && !r.already_done && r.coins_earned > 0) {
      haptic.impact();
      coinToast.value = { show: true, amount: r.coins_earned, total: r.coins_total };
      sendMetric('DailyCheckin');
      clearTimeout(coinToastTimer);
      coinToastTimer = setTimeout(() => { coinToast.value = { ...coinToast.value, show: false }; }, 4200);
    }
  } catch (e) {
    console.error('❌ Ошибка ежедневного чекина:', e);
  } finally {
    checkinInFlight = false;
  }
};

const openRouletteFromToast = () => {
  coinToast.value = { ...coinToast.value, show: false };
  sendMetric('CoinToast-Roulette');
  goTo('roulette');
};

const refreshDataInBackground = () => {
  isRefreshing.value = true;
  fetchAndCacheUserInfo(true)
      .then(() => { loadDataFromSession(); runDailyCheckin(); loadCoins(); })
      .catch((error) => console.error("❌ Ошибка при обновлении данных в фоне:", error))
      .finally(() => { isRefreshing.value = false; });
};

const sendMetric = (buttonName) => {
  try {
    const token = sessionStorage.getItem("jwtToken");
    if (!token) return;

    const eventname = `Home-${buttonName}-${botName.value}`;

    fetch('https://baaack.zabanishumresh.com/addevent', {
      method: 'POST',
      headers: { 'Authorization': token, 'Content-Type': 'application/json' },
      body: JSON.stringify({eventname}),
      keepalive: true
    }).catch(error => console.error("Ошибка при отправке метрики:", error));
  } catch (error) {
    console.error("Ошибка при отправке метрики:", error);
  }
};

const goTo = (route) => {
  haptic.impact();
  router.push(`/${route}`);
};

const handleDepositClick = () => { sendMetric('Deposit-btn'); goTo('deposit'); };
const handlePaymentHistoryClick = () => { sendMetric('PaymentHistory-btn'); goTo('payment-history'); };

const handleConnectionClick = (tariffName) => {
  sendMetric(`Connect-${tariffName}`);
  if (showOnboardingTour.value) skipTour();
  goTo('connection');
};

const handleBuySubscriptionClick = () => { sendMetric('BuySubscription'); goTo('buyVPN'); };

// ✅ Продление истёкшего тарифа
const handleRenewClick = (tariff) => {
  const key = getTariffKey(tariff?.name) || '';
  sendMetric(`Renew-${key || 'Unknown'}`);
  haptic.impact();
  router.push({ path: '/buyVPN', query: key ? { tariff: key.toLowerCase() } : {} });
};
const handleReferralClick = () => { sendMetric('Referral'); goTo('referal'); };
const handleTasksClick = () => { sendMetric('Tasks'); goTo('tasks'); };

const handleTouchStart = (e) => { touchStartX.value = e.touches[0].clientX; };

const handleTouchMove = (e) => {
  const diff = e.touches[0].clientX - touchStartX.value;
  if (diff < 0) {
    swipeOffset.value = diff;
    notificationOpacity.value = 1 + (diff / 300);
  }
};

const handleTouchEnd = () => {
  if (swipeOffset.value < -100) dismissNotification();
  else {
    swipeOffset.value = 0;
    notificationOpacity.value = 1;
  }
};

const dismissNotification = () => {
  sendMetric('DismissNotification');
  swipeOffset.value = -500;
  notificationOpacity.value = 0;
  setTimeout(() => { isNotificationDismissed.value = true; }, 300);
};

const closeWelcomePopup = () => {
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('WelcomePopup-Closed');

  nextTick(() => {
    if (route.path === '/' && isNewUser.value && isFreeTariffAvailable.value) {
      setTimeout(() => { if (route.path === '/') startOnboardingTour(); }, 500);
    }
  });
};

const openTasksFromPopup = () => {
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('WelcomePopup-OpenTasks');
  goTo('tasks');
};

const startOnboardingTour = () => {
  if (!connectButtonRef.value || !freeTariffRef.value) return;

  try {
    const buttonRect = connectButtonRef.value.getBoundingClientRect();
    const cardRect = freeTariffRef.value.getBoundingClientRect();

    spotlightStyle.value = {
      top: `${cardRect.top - 8}px`,
      left: `${cardRect.left - 8}px`,
      width: `${cardRect.width + 16}px`,
      height: `${cardRect.height + 16}px`,
    };

    tooltipStyle.value = {
      top: `${buttonRect.bottom + 20}px`,
      left: '50%',
      transform: 'translateX(-50%)',
    };

    showOnboardingTour.value = true;
    sendMetric('OnboardingTour-Started');
  } catch (error) {
    console.error("❌ Ошибка при запуске тура:", error);
  }
};

const skipTour = () => {
  showOnboardingTour.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('OnboardingTour-Skipped');
};

watch(isFreeTariffAvailable, (newVal) => {
  if (newVal && isNewUser.value && !showWelcomePopup.value && !showOnboardingTour.value && route.path === '/') {
    nextTick(() => {
      setTimeout(() => { if (route.path === '/') startOnboardingTour(); }, 800);
    });
  }
});

function getDaysWord(days) {
  if (days % 10 === 1 && days % 100 !== 11) return "День";
  else if ([2, 3, 4].includes(days % 10) && ![12, 13, 14].includes(days % 100)) return "Дня";
  else return "Дней";
}

/* ===== Upgrade flow ===== */
const userName = ref('друг');

const toast = ref({ show: false, msg: '' });
let toastTimer = null;
const showToast = (msg) => {
  toast.value = { show: true, msg };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = { ...toast.value, show: false }; }, 2200);
};

const TARIFF_MONTHLY = { Basic: 149, Medium: 249, Premium: 399 };
const TARIFF_RANK = { Basic: 1, Medium: 2, Premium: 3 };
const WL_MONTH = 100;

const tariffKeyFromName = (name) => getTariffKey(name);
const nextTariffKey = (key) => {
  const order = ['Basic', 'Medium', 'Premium'];
  const i = order.indexOf(key);
  return (i >= 0 && i < order.length - 1) ? order[i + 1] : null;
};
const nextTariffName = (name) => {
  const key = tariffKeyFromName(name);
  return key ? nextTariffKey(key) : null;
};

const upgradeOpen = ref(false);
const upgradeFromKey = ref('Medium');
const upgradeToKey = ref('Premium');
const upgradeBypass = ref(false);
const upgradeRemainingDays = ref(18);
const upgradeLoading = ref(false);
let upgradeTariffIndex = -1;

const clampDays = (d) => Math.max(1, Math.floor(d || 1));
const emojiByKey = (k) => k === 'Premium' ? '💎' : k === 'Medium' ? '🚀' : '⚡';

const upgradeProration = computed(() => {
  const portion = clampDays(upgradeRemainingDays.value) / 30;
  const from = TARIFF_MONTHLY[upgradeFromKey.value] || 0;
  const to = TARIFF_MONTHLY[upgradeToKey.value] || 0;
  const base = Math.round((to - from) * portion);
  const bypass = upgradeBypass.value ? Math.round(WL_MONTH * portion) : 0;
  return { base, bypass, total: base + bypass };
});
const upgradeBypassSurcharge = computed(() => Math.round(WL_MONTH * clampDays(upgradeRemainingDays.value) / 30));
const upgradeCanAfford = computed(() => (balance.value ?? 0) >= upgradeProration.value.total);
const upgradeAfterBalance = computed(() => Math.max(0, (balance.value ?? 0) - upgradeProration.value.total));

const openUpgrade = (tariff) => {
  haptic.impact();
  const key = tariffKeyFromName(tariff.name);
  const next = key ? nextTariffKey(key) : null;
  if (!next) { showToast('У вас максимальный тариф'); return; }
  upgradeFromKey.value = key;
  upgradeToKey.value = next;
  upgradeBypass.value = false;
  upgradeRemainingDays.value = clampDays(tariff.daysRemaining);
  upgradeTariffIndex = uniqueTariffs.value.indexOf(tariff);
  upgradeOpen.value = true;
  sendMetric(`OpenUpgrade-${key}`);
};
const closeUpgrade = () => { upgradeOpen.value = false; };
const toggleUpgradeBypass = () => { upgradeBypass.value = !upgradeBypass.value; };

const confirmUpgrade = async () => {
  const { total } = upgradeProration.value;
  if (TARIFF_RANK[upgradeToKey.value] <= TARIFF_RANK[upgradeFromKey.value]) { showToast('Некорректный тариф'); return; }
  if (!upgradeCanAfford.value) { showToast('Недостаточно средств на балансе'); return; }
  upgradeLoading.value = true;
  try {
    const res = await fetch(`${API_BASE}/upgradeTariff`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: buildAuthHeader()
      },
      body: JSON.stringify({
        from_tariff: upgradeFromKey.value.toLowerCase(),
        to_tariff: upgradeToKey.value.toLowerCase(),
        with_bypass: upgradeBypass.value
      })
    });
    if (res.status === 200) {
      const newBal = Math.max(0, (balance.value ?? 0) - total);
      balance.value = newBal;
      sessionStorage.setItem('balance', String(newBal));
      if (upgradeTariffIndex >= 0 && uniqueTariffs.value[upgradeTariffIndex]) {
        uniqueTariffs.value[upgradeTariffIndex].name = addEmojiToTariff(upgradeToKey.value);
      }
      upgradeOpen.value = false;
      showToast(`Тариф улучшен до ${upgradeToKey.value} · −${total} ₽`);
      sendMetric(`Upgrade-Success-${upgradeToKey.value}`);
    } else if (res.status === 400) { showToast('Некорректный тариф'); }
    else if (res.status === 402) { showToast('Недостаточно средств на балансе'); }
    else if (res.status === 404) { showToast('Профиль не найден'); }
    else if (res.status === 406) { showToast('Текущий тариф не активен'); }
    else { showToast('Не удалось улучшить тариф'); }
  } catch (e) {
    console.error('Upgrade error', e);
    showToast('Ошибка сети, попробуйте позже');
  } finally {
    upgradeLoading.value = false;
  }
};

onMounted(() => {
  disableSwipes();

  isNotificationDismissed.value = false;
  swipeOffset.value = 0;
  notificationOpacity.value = 1;

  loadDataFromSession();
  startBannerAutoplay();
  window.addEventListener('resize', updateBannerOffset);

  try {
    const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
    if (tgUser?.first_name) userName.value = tgUser.first_name;
  } catch (e) { /* noop */ }

  const isTelegramWebApp = window.Telegram && window.Telegram.WebApp;

  if (isTelegramWebApp) {
    const webApp = window.Telegram.WebApp;
    const initData = webApp.initData || "";

    if (initData === "") {
      router.push('/welcome');
      return;
    }

    const telegramId = webApp.initDataUnsafe.user?.id;
    webApp.setHeaderColor("#F5F7F6");
    sessionStorage.setItem("tg_id", telegramId);

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => refreshDataInBackground(), {timeout: 500});
    } else {
      setTimeout(() => refreshDataInBackground(), 100);
    }

    fetchDevices();
    runDailyCheckin(); // ✅ если токен уже есть — сразу; иначе после refreshDataInBackground
    loadCoins();

    if (isNewUser.value) {
      showWelcomePopup.value = true;
      sendMetric('WelcomePopup-Shown');
    }
  } else {
    router.push('/welcome');
  }
});

onUnmounted(() => {
  stopBannerAutoplay();
  window.removeEventListener('resize', updateBannerOffset);
  clearTimeout(toastTimer);
  clearTimeout(coinToastTimer);
});
</script>

<style scoped>
.home {
  animation: scrIn .32s ease;
  padding: 22px 18px 120px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}
.home > * { flex-shrink: 0; }
@keyframes scrIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

/* Header */
.home-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.hi-wrap { min-width: 0; flex: 1; }
.hi-title { font-size: 22px; font-weight: 800; letter-spacing: -.02em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--ink); }
.hi-sub { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
.avatar-btn {
  width: 46px; height: 46px; border-radius: 15px; background: #fff; border: 1px solid var(--border-2);
  display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(16,24,20,.05);
  cursor: pointer; flex-shrink: 0; transition: transform .12s;
}
.avatar-btn:active { transform: scale(.94); }

/* Balance */
.balance-card {
  background: var(--ink); border-radius: 22px; padding: 20px; position: relative; overflow: hidden;
  box-shadow: 0 12px 30px rgba(15,21,18,.22);
}
.balance-glow { position: absolute; right: -40px; top: -40px; width: 150px; height: 150px; border-radius: 50%; background: radial-gradient(circle, rgba(18,183,106,.45), transparent 70%); }
.balance-top { display: flex; align-items: center; justify-content: space-between; position: relative; }
.balance-label { font-size: 13px; color: rgba(255,255,255,.6); font-weight: 600; }
.history-btn { border: none; cursor: pointer; width: 34px; height: 34px; border-radius: 10px; background: rgba(255,255,255,.1); color: #fff; display: flex; align-items: center; justify-content: center; transition: transform .12s; }
.history-btn:active { transform: scale(.9); }
.balance-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 8px 0 16px; position: relative; }
.balance-amount { font-size: 38px; font-weight: 800; letter-spacing: -.03em; color: #fff; position: relative; min-width: 0; }
.coins-chip {
  flex-shrink: 0; display: flex; align-items: center; gap: 8px; padding: 8px 12px 8px 8px; border-radius: 14px; cursor: pointer;
  font-family: inherit; border: 1px solid rgba(247,194,26,.35);
  background: linear-gradient(135deg, rgba(247,194,26,.22), rgba(247,194,26,.06));
  transition: transform .15s ease;
}
.coins-chip:active { transform: scale(.95); }
.coins-chip-text { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.1; }
.coins-chip-num { font-size: 15px; font-weight: 800; color: #fff; }
.coins-chip-cta { font-size: 11px; font-weight: 700; color: #FFD95C; }
.balance-amount .rub { font-size: 22px; color: rgba(255,255,255,.5); }
.balance-cta {
  width: 100%; border: none; cursor: pointer; padding: 14px; border-radius: 14px; background: var(--green); color: #fff;
  font-family: inherit; font-size: 15px; font-weight: 800; box-shadow: 0 8px 20px rgba(18,183,106,.36);
  transition: transform .12s; display: flex; align-items: center; justify-content: center; gap: 8px; position: relative;
}
.balance-cta:active { transform: scale(.98); }

/* Banners */
.banners-section { border-radius: 20px; }
.banners-container { width: 100%; overflow: hidden; border-radius: 20px; position: relative; }
.banners-track { display: flex; transition: transform .35s cubic-bezier(.4,0,.2,1); will-change: transform; }
.banner-item { min-width: 100%; cursor: pointer; user-select: none; -webkit-user-select: none; }
.banner-image { width: 100%; height: auto; display: block; border-radius: 20px; object-fit: cover; aspect-ratio: 1280/442; background: var(--chip-bg); }
.banner-dots { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); display: flex; gap: 7px; z-index: 10; padding: 6px 10px; background: rgba(15,21,18,.25); backdrop-filter: blur(8px); border-radius: 20px; }
.dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,.6); cursor: pointer; transition: all .3s ease; }
.dot.active { background: #fff; width: 18px; border-radius: 3px; }

/* Subscription card */
.sub-card { background: #fff; border: 1px solid var(--border); border-radius: 22px; padding: 16px; box-shadow: 0 4px 16px rgba(16,24,20,.05); }
.sub-skeleton { height: 190px; background: linear-gradient(100deg, #f1f4f2 30%, #f8faf9 50%, #f1f4f2 70%); background-size: 200% 100%; animation: shimmerBg 1.3s infinite; }
@keyframes shimmerBg { to { background-position: -200% 0; } }
.sub-inner { border: 1.5px solid var(--border); border-radius: 18px; padding: 16px; background: var(--surface-alt); }
.sub-row { display: flex; align-items: center; justify-content: space-between; }
.sub-title { font-size: 18px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.badge-active { font-size: 11px; font-weight: 800; color: var(--green-dark); background: var(--success-bg); padding: 5px 11px; border-radius: 999px; }
.badge-active.expired { color: #B4890B; background: #FEF3D3; }
.chips { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 12px; }
.chip { font-size: 12px; font-weight: 600; color: #4B5651; background: var(--chip-bg); border: 1px solid var(--border-3); padding: 5px 10px; border-radius: 9px; }
/* ✅ Чип с докупленными слотами */
.chip.chip-extra { color: var(--green-dark); background: var(--mint-bg); border-color: var(--mint-border); font-weight: 700; }
.progress-wrap { margin-top: 14px; }
.progress-track { height: 8px; border-radius: 999px; background: var(--track-bg); overflow: hidden; }
.progress-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, var(--green), var(--green-dark)); transition: width .4s ease; }
.progress-label { font-size: 12px; color: var(--text-muted); font-weight: 600; margin-top: 7px; }
.expired-note { font-size: 13px; color: var(--text-muted); font-weight: 600; margin-top: 12px; }
.sub-actions { display: flex; gap: 9px; margin-top: 14px; }
.btn-connect {
  flex: 1; border: none; cursor: pointer; padding: 13px; border-radius: 13px; background: var(--green); color: #fff;
  font-family: inherit; font-size: 14px; font-weight: 800; box-shadow: 0 6px 16px rgba(18,183,106,.3);
  transition: transform .12s; display: flex; align-items: center; justify-content: center; gap: 7px;
}
.btn-connect:active { transform: scale(.98); }

/* ✅ Кнопка устройств + состояния лимита */
.btn-devices {
  position: relative; border: 1.5px solid #DAE4DF; cursor: pointer; padding: 13px 15px; border-radius: 13px;
  background: #fff; color: var(--ink); font-family: inherit; font-size: 14px; font-weight: 800;
  transition: transform .12s, border-color .2s, background .2s, color .2s;
  display: flex; align-items: center; gap: 7px; white-space: nowrap;
}
.btn-devices:active { transform: scale(.98); }
.btn-devices.limit { border-color: #B4890B; background: #FEF3D3; color: #B4890B; }
.btn-devices.over { border-color: var(--danger); background: var(--danger-bg); color: var(--danger); animation: pulseWarn 1.8s infinite; }
@keyframes pulseWarn {
  0%, 100% { box-shadow: 0 0 0 0 rgba(229,72,77,.35); }
  50% { box-shadow: 0 0 0 6px rgba(229,72,77,0); }
}
.dev-warn-badge {
  position: absolute; top: -6px; right: -6px; width: 18px; height: 18px; border-radius: 50%;
  background: currentColor; color: #fff; font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center; border: 2px solid var(--surface-alt);
}
.btn-devices.limit .dev-warn-badge { background: #B4890B; }
.btn-devices.over .dev-warn-badge { background: var(--danger); }

/* ✅ Подсказка о лимите на карточке */
.limit-alert {
  display: flex; align-items: flex-start; gap: 9px; margin-top: 12px; padding: 11px 12px; border-radius: 13px;
}
.limit-alert.limit { background: #FEF3D3; color: #B4890B; border: 1px solid rgba(180,137,11,.25); }
.limit-alert.over { background: var(--danger-bg); color: var(--danger); border: 1px solid rgba(229,72,77,.25); }
.limit-alert-icon { font-size: 16px; line-height: 1; }
.limit-alert-body { flex: 1; min-width: 0; }
.limit-alert-title { font-size: 13px; font-weight: 800; }
.limit-alert-text { font-size: 11.5px; font-weight: 600; opacity: .8; margin-top: 1px; }

/* ✅ Кнопка «Добавить устройства» */
.btn-add-slots {
  width: 100%; margin-top: 9px; border: none; cursor: pointer; padding: 12px; border-radius: 13px;
  background: var(--ink); color: #fff; font-family: inherit; font-size: 13.5px; font-weight: 800;
  transition: transform .12s; display: flex; align-items: center; justify-content: center; gap: 7px;
}
.btn-add-slots:active { transform: scale(.98); }

.btn-upgrade {
  width: 100%; margin-top: 9px; border: 1px dashed rgba(18,183,106,.5); cursor: pointer; padding: 12px; border-radius: 13px;
  background: var(--mint-bg); color: var(--green-dark); font-family: inherit; font-size: 13.5px; font-weight: 800;
  transition: transform .12s; display: flex; align-items: center; justify-content: center; gap: 7px;
}
.btn-upgrade:active { transform: scale(.98); }
.btn-upgrade.max { border-color: #D8DEDA; background: var(--app-bg); color: var(--text-faint); }

/* ✅ Кнопка продления истёкшего тарифа */
.btn-renew {
  width: 100%; margin-top: 14px; border: none; cursor: pointer; padding: 13px; border-radius: 13px;
  background: var(--green); color: #fff; font-family: inherit; font-size: 14px; font-weight: 800;
  box-shadow: 0 6px 16px rgba(18,183,106,.3); transition: transform .12s;
  display: flex; align-items: center; justify-content: center; gap: 7px;
}
.btn-renew:active { transform: scale(.98); }

/* ✅ Архив тарифов */
.archive { display: flex; flex-direction: column; }
.archive-head {
  width: 100%; display: flex; align-items: center; gap: 11px; padding: 13px 15px; border-radius: 18px;
  background: #fff; border: 1px solid var(--border); cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 16px rgba(16,24,20,.04); transition: transform .12s;
}
.archive-head:active { transform: scale(.99); }
.archive-emoji { font-size: 15px; }
.archive-head-info { flex: 1; text-align: left; }
.archive-title { font-size: 14px; font-weight: 800; color: var(--ink); }
.archive-sub { font-size: 11.5px; font-weight: 600; color: var(--text-faint); margin-top: 1px; }
.archive-chevron { display: flex; color: var(--text-muted); transition: transform .22s ease; }
.archive-chevron.open { transform: rotate(180deg); }
.archive-list { display: flex; flex-direction: column; gap: 10px; margin-top: 10px; animation: scrIn .24s ease; }
.archive-card {
  background: #fff; border: 1px solid var(--border); border-radius: 18px; padding: 14px 15px;
  display: flex; align-items: center; gap: 12px; opacity: .9;
}
.archive-card-info { flex: 1; min-width: 0; }
.archive-card-name { font-size: 15px; font-weight: 800; color: var(--text-muted); letter-spacing: -.01em; }
.archive-card-date { font-size: 11.5px; font-weight: 600; color: var(--text-faint); margin-top: 2px; }
.archive-renew {
  border: 1px solid #DAE4DF; background: #fff; color: var(--ink); font-family: inherit; font-size: 13px;
  font-weight: 800; padding: 10px 14px; border-radius: 12px; cursor: pointer; white-space: nowrap; transition: transform .12s;
}
.archive-renew:active { transform: scale(.97); }

/* Empty state */
.empty-state { background: #fff; border: 1px solid var(--border); border-radius: 22px; text-align: center; padding: 40px 20px; box-shadow: 0 4px 16px rgba(16,24,20,.05); }
.empty-icon { font-size: 44px; }
.empty-title { font-size: 18px; font-weight: 800; margin-top: 10px; color: var(--ink); }
.empty-text { font-size: 14px; color: var(--text-muted); margin: 6px 0 18px; }
.btn-buy { border: none; cursor: pointer; padding: 15px 24px; border-radius: 14px; background: var(--green); color: #fff; font-family: inherit; font-size: 15px; font-weight: 800; box-shadow: 0 8px 20px rgba(18,183,106,.32); transition: transform .12s; }
.btn-buy:active { transform: scale(.98); }

/* Legal */
.legal-links { display: flex; justify-content: center; align-items: center; gap: 8px; font-size: 11px; color: var(--text-faint); margin-top: 2px; flex-wrap: wrap; }
.legal-links a { color: var(--text-faint); text-decoration: none; }
.legal-links .separator { color: var(--hairline); }

/* ===== Bottom sheets (liquid glass) ===== */
.sheet-overlay { position: fixed; inset: 0; z-index: 10001; display: flex; align-items: flex-end; justify-content: center; background: rgba(15,21,18,.5); backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px); animation: fadeIn .2s ease; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.sheet {
  width: 100%; max-width: 480px; background: rgba(255,255,255,.9); backdrop-filter: blur(30px) saturate(180%); -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-top: 1px solid rgba(255,255,255,.9); border-radius: 28px 28px 0 0; padding: 22px 20px calc(22px + env(safe-area-inset-bottom));
  box-shadow: 0 -12px 50px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.9); animation: sheetIn .32s cubic-bezier(.16,1,.3,1); max-height: 88vh; overflow-y: auto;
}
@keyframes sheetIn { from { opacity: 0; transform: translateY(40px) scale(.98); } to { opacity: 1; transform: none; } }
.sheet-handle { width: 40px; height: 4px; border-radius: 999px; background: #E2E7E4; margin: 0 auto 14px; }
.sheet-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.sheet-title { font-size: 18px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.sheet-sub { font-size: 13px; color: var(--text-muted); font-weight: 600; margin-top: 3px; }
.sheet-close { width: 32px; height: 32px; border-radius: 50%; border: none; cursor: pointer; background: var(--chip-bg2); color: var(--text-muted); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

/* ✅ Счётчик слотов в шторке */
.slot-meter { padding: 13px 14px; border-radius: 15px; background: var(--surface-alt2); border: 1px solid var(--chip-bg); margin-bottom: 14px; }
.slot-meter.limit { background: #FEF3D3; border-color: rgba(180,137,11,.25); }
.slot-meter.over { background: var(--danger-bg); border-color: rgba(229,72,77,.25); }
.slot-meter-top { display: flex; align-items: center; justify-content: space-between; font-size: 12.5px; font-weight: 700; color: #4B5651; }
.slot-meter-top b { font-size: 13px; font-weight: 800; color: var(--ink); }
.slot-meter.limit .slot-meter-top b { color: #B4890B; }
.slot-meter.over .slot-meter-top b { color: var(--danger); }
.slot-bars { display: flex; gap: 5px; margin-top: 9px; }
.slot-bar { flex: 1; height: 7px; border-radius: 999px; background: #DDE4E0; }
.slot-bar.filled { background: var(--green); }
.slot-bar.filled-extra { background: var(--green-dark); }
.slot-bar.beyond { background: var(--danger); }
.slot-meter-hint { font-size: 11.5px; font-weight: 600; color: var(--text-muted); margin-top: 9px; line-height: 1.45; }

.devices-loading, .devices-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 30px 0; color: var(--text-muted); font-size: 14px; font-weight: 600; text-align: center; }
.devices-list { display: flex; flex-direction: column; gap: 10px; }
.device-card { display: flex; align-items: center; gap: 12px; background: var(--surface-alt2); border: 1px solid var(--chip-bg); border-radius: 15px; padding: 13px 14px; }
.device-card.blocked { background: var(--danger-bg); border-color: rgba(229,72,77,.22); }
.device-blocked-tag { font-size: 10.5px; font-weight: 800; color: var(--danger); background: #fff; padding: 5px 8px; border-radius: 8px; white-space: nowrap; }
.device-icon { width: 40px; height: 40px; border-radius: 12px; background: #fff; border: 1px solid var(--border-3); display: flex; align-items: center; justify-content: center; font-size: 19px; flex-shrink: 0; }
.device-info { flex: 1; min-width: 0; }
.device-model { font-size: 14px; font-weight: 700; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.device-meta { font-size: 11.5px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.device-actions { display: flex; gap: 8px; flex-shrink: 0; }
.dev-del, .dev-yes, .dev-no { width: 36px; height: 36px; border-radius: 11px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: transform .12s; }
.dev-del { background: var(--danger-bg); color: var(--danger); }
.device-card.blocked .dev-del { background: #fff; }
.dev-yes { background: var(--danger); color: #fff; }
.dev-no { background: var(--chip-bg2); color: var(--text-muted); }
.dev-del:active, .dev-yes:active, .dev-no:active { transform: scale(.9); }
.sheet-hint { font-size: 11.5px; color: var(--text-faint); text-align: center; margin-top: 14px; line-height: 1.4; }

/* ✅ Блок покупки слотов */
.slots-buy { margin-top: 16px; padding: 16px; border-radius: 18px; background: var(--mint-bg); border: 1px solid var(--mint-border); }
.slots-buy-head { display: flex; align-items: center; gap: 10px; }
.slots-buy-emoji { font-size: 20px; }
.slots-buy-title { font-size: 15px; font-weight: 800; color: var(--ink); }
.slots-buy-sub { font-size: 12px; color: var(--text-muted); font-weight: 600; }
.stepper { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 14px; padding: 10px 12px; background: #fff; border: 1px solid var(--border-3); border-radius: 14px; }
.step-btn { width: 40px; height: 40px; border-radius: 12px; border: 1px solid var(--border-3); background: #fff; color: var(--ink); font-size: 22px; font-weight: 800; font-family: inherit; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: transform .12s, color .2s; }
.step-btn:active { transform: scale(.92); }
.step-btn.off { color: #C9D2CD; }
.step-value { text-align: center; }
.step-num { font-size: 22px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.step-hint { font-size: 11px; font-weight: 600; color: var(--text-faint); }
.slots-summary { margin-top: 12px; display: flex; flex-direction: column; gap: 7px; }
.slots-row { display: flex; align-items: center; justify-content: space-between; font-size: 13px; font-weight: 600; color: var(--text-muted); }
.slots-row b { font-size: 14px; font-weight: 800; color: var(--ink); }
.slots-row b.big { font-size: 18px; letter-spacing: -.02em; }
.slots-row.muted { font-size: 12px; color: var(--text-faint); }
.slots-row.muted b { font-size: 13px; }
.slots-row b.danger { color: var(--danger); }
.slots-cta { width: 100%; margin-top: 14px; border: none; cursor: pointer; padding: 15px; border-radius: 15px; background: var(--green); color: #fff; font-family: inherit; font-size: 15px; font-weight: 800; box-shadow: 0 10px 24px rgba(18,183,106,.32); transition: transform .12s; display: flex; align-items: center; justify-content: center; }
.slots-cta:active { transform: scale(.98); }
.slots-cta.disabled { background: #B7C4BD; box-shadow: none; }
.slots-note { font-size: 11px; color: var(--text-faint); text-align: center; margin-top: 10px; line-height: 1.45; }

/* Upgrade sheet */
.up-current { display: flex; align-items: center; gap: 10px; margin-top: 16px; padding: 12px 14px; background: var(--chip-bg2); border: 1px solid var(--border-3); border-radius: 14px; }
.up-emoji { font-size: 20px; }
.up-current-info { flex: 1; }
.up-label { font-size: 12px; color: var(--text-muted); font-weight: 600; }
.up-name { font-size: 15px; font-weight: 800; color: var(--ink); }
.up-arrow { color: var(--hairline); font-size: 20px; }
.up-option { width: 100%; margin-top: 12px; border: 1.5px solid var(--green); background: rgba(18,183,106,.08); cursor: pointer; padding: 15px; border-radius: 16px; display: flex; align-items: center; gap: 12px; font-family: inherit; text-align: left; }
.up-option-info { flex: 1; }
.up-option-name { font-size: 16px; font-weight: 800; color: var(--ink); }
.up-option-name.sm { font-size: 14px; font-weight: 700; }
.up-option-desc { font-size: 12px; color: var(--text-muted); }
.up-option-price { text-align: right; }
.up-surcharge { font-size: 15px; font-weight: 800; color: var(--green); }
.up-surcharge-label { font-size: 11px; color: var(--text-faint); font-weight: 600; }
.up-bypass { cursor: pointer; margin-top: 12px; display: flex; align-items: center; gap: 12px; padding: 14px 15px; background: #fff; border: 1px solid var(--border); border-radius: 15px; }
.toggle { width: 46px; height: 27px; border-radius: 999px; background: #D8DEDA; position: relative; transition: background .25s ease; flex-shrink: 0; }
.toggle.on { background: var(--green); }
.toggle .knob { position: absolute; top: 3px; left: 3px; width: 21px; height: 21px; border-radius: 50%; background: #fff; box-shadow: 0 2px 5px rgba(0,0,0,.2); transition: transform .25s cubic-bezier(.34,1.42,.5,1); }
.toggle.on .knob { transform: translateX(19px); }
.up-summary { margin-top: 16px; padding: 15px; background: var(--surface-alt2); border: 1px solid var(--chip-bg); border-radius: 16px; }
.up-summary-row { display: flex; align-items: center; justify-content: space-between; font-size: 13px; color: var(--text-muted); font-weight: 600; }
.up-summary-row b { font-size: 20px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.up-summary-row.muted { margin-top: 8px; }
.up-summary-row.muted span { font-size: 12px; color: var(--text-faint); }
.up-summary-row.muted b { font-size: 13px; font-weight: 700; color: var(--ink); }
.up-summary-row b.danger { color: var(--danger); }
.up-cta { width: 100%; margin-top: 14px; border: none; cursor: pointer; padding: 16px; border-radius: 15px; background: var(--green); color: #fff; font-family: inherit; font-size: 16px; font-weight: 800; box-shadow: 0 10px 24px rgba(18,183,106,.32); transition: transform .12s; display: flex; align-items: center; justify-content: center; }
.up-cta:active { transform: scale(.98); }
.up-cta.disabled { background: #B7C4BD; box-shadow: none; }

/* Onboarding */
.onboarding-overlay { position: fixed; inset: 0; background: rgba(15,21,18,.75); z-index: 9998; backdrop-filter: blur(4px); animation: fadeIn .3s ease; }
.onboarding-spotlight { position: absolute; background: transparent; border-radius: 18px; box-shadow: 0 0 0 9999px rgba(15,21,18,.75); z-index: 9999; pointer-events: none; transition: all .3s ease; }
.onboarding-tooltip { position: fixed; background: linear-gradient(135deg, var(--green), var(--green-dark)); border-radius: 20px; padding: 22px; z-index: 10000; max-width: 320px; box-shadow: 0 20px 50px rgba(18,183,106,.4); }
.tooltip-content { text-align: center; }
.tooltip-icon { font-size: 42px; margin-bottom: 10px; }
.tooltip-title { font-size: 19px; font-weight: 800; color: #fff; margin-bottom: 6px; }
.tooltip-text { font-size: 14px; color: rgba(255,255,255,.95); line-height: 1.5; margin-bottom: 14px; }
.tooltip-btn { background: #fff; color: var(--green-dark); padding: 12px 24px; border-radius: 12px; font-size: 15px; font-weight: 800; border: none; cursor: pointer; width: 100%; }

/* Toast */
.toast { position: fixed; top: 16px; left: 50%; transform: translateX(-50%); z-index: 10002; max-width: 400px; width: calc(100% - 32px); padding: 14px 18px; border-radius: 14px; font-size: 14px; font-weight: 700; text-align: center; box-shadow: 0 14px 36px rgba(0,0,0,.2); background: var(--green); color: #fff; animation: toastIn .28s ease; }
@keyframes toastIn { from { opacity: 0; transform: translate(-50%,-16px); } to { opacity: 1; transform: translate(-50%,0); } }

/* Spinners & skeleton */
.spinner { width: 20px; height: 20px; border: 2px solid rgba(18,183,106,.2); border-top-color: var(--green); border-radius: 50%; animation: spin .8s linear infinite; }
.spinner-sm { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.5); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }
.balance-amount.skeleton { color: transparent; background: rgba(255,255,255,.12); border-radius: 8px; width: 140px; }
button:disabled { opacity: .6; cursor: not-allowed; }

/* ✅ Тост «+5 монет за вход» */
.coin-toast {
  position: fixed; top: 16px; left: 50%; transform: translateX(-50%); z-index: 10002;
  width: calc(100% - 32px); max-width: 400px; box-sizing: border-box;
  display: flex; align-items: center; gap: 12px; padding: 12px 16px 12px 12px; border: none; cursor: pointer;
  border-radius: 18px; background: var(--ink); color: #fff; font-family: inherit; text-align: left;
  box-shadow: 0 16px 40px rgba(15,21,18,.35);
}
.coin-toast-icon { animation: coinToastSpin 1s cubic-bezier(.34,1.42,.5,1); filter: drop-shadow(0 3px 5px rgba(0,0,0,.35)); }
.coin-toast-body { flex: 1; min-width: 0; }
.coin-toast-title { font-size: 15px; font-weight: 800; color: #FACC15; }
.coin-toast-sub { font-size: 12px; font-weight: 600; color: rgba(255,255,255,.7); margin-top: 1px; }
.coin-toast-enter-active { animation: coinToastIn .45s cubic-bezier(.34,1.42,.5,1); }
.coin-toast-leave-active { transition: opacity .25s ease, transform .25s ease; }
.coin-toast-leave-to { opacity: 0; transform: translate(-50%, -16px); }
@keyframes coinToastIn { from { opacity: 0; transform: translate(-50%, -24px) scale(.94); } to { opacity: 1; transform: translate(-50%, 0) scale(1); } }
@keyframes coinToastSpin { 0% { transform: rotateY(0) scale(.6); } 100% { transform: rotateY(720deg) scale(1); } }
</style>
